/**
 * Las tareas programadas del asistente en el chat (D65): la tarjeta en la nota que la confirma, con
 * «Cambiar hora» y «Cancelar», y la franja con las pendientes de la conversación.
 */
import { type ReactNode, useEffect, useMemo, useState } from "react";

import { ClockCircleOutlined } from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Flex,
  Popconfirm,
  Popover,
  Space,
  Tag,
  TimePicker,
  Tooltip,
  Typography,
} from "antd";
import dayjs, { Dayjs } from "dayjs";

import { Robot2Line } from "@/components/icon/RobotIcon";
import type { NoteTask, TaskView } from "@/services/conversations.api";

import {
  errorDeHora,
  estadoDeTarea,
  ESTADOS_DE_TAREA,
  fechaDeTarea,
  horaArgentina,
  partesEnArgentina,
  sePuedeCambiar,
  TAREA_MAX_DIAS,
  tareasDeLaFranja,
  textoDelEstado,
} from "./agent-tasks";

const { Text } = Typography;

/** Los mismos azules que los mensajes programados: lo que va a pasar más tarde. */
const AZUL = "#2F54EB";
const AZUL_FONDO = "#F0F5FF";
const AZUL_BORDE = "#D6E4FF";

/** La hora de ahora, renovada cada `cadaMs` mientras `activo`: una pendiente pasa sola a atrasada. */
function useAhora(activo: boolean, cadaMs = 30_000): Date {
  const [ahora, setAhora] = useState(() => new Date());
  useEffect(() => {
    if (!activo) return;
    setAhora(new Date());
    const t = setInterval(() => setAhora(new Date()), cadaMs);
    return () => clearInterval(t);
  }, [activo, cadaMs]);
  return ahora;
}

function EtiquetaDeEstado({
  tarea,
  ahora,
}: {
  tarea: Pick<NoteTask, "status" | "runAt" | "cancelledByName" | "cancelledAt">;
  ahora: Date;
}) {
  const estado = estadoDeTarea(tarea, ahora);
  const tag = (
    <Tag color={ESTADOS_DE_TAREA[estado]?.color} style={{ margin: 0 }}>
      {textoDelEstado(estado, tarea.cancelledByName)}
    </Tag>
  );
  return estado === "cancelled" && tarea.cancelledAt ? (
    <Tooltip title={`Cancelada el ${fechaDeTarea(tarea.cancelledAt, ahora)}`}>
      {tag}
    </Tooltip>
  ) : (
    tag
  );
}

/**
 * «Cambiar hora»: el día y la hora en hora de Argentina, con los mismos selectores que «Programar
 * mensaje». Abre con la hora actual de la tarea; guardar valida lo mismo que la API. Lo usa también
 * la pestaña «Tareas» del asistente, para las de una ficha del historial (D66).
 */
export function CambiarHora({
  runAt,
  onGuardar,
  children,
}: {
  runAt: string;
  onGuardar: (runAt: string) => Promise<boolean>;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [dia, setDia] = useState<Dayjs | null>(null);
  const [hora, setHora] = useState<Dayjs | null>(null);
  const [guardando, setGuardando] = useState(false);

  const abrir = (v: boolean) => {
    setOpen(v);
    if (!v) return;
    // Los selectores trabajan en la hora del navegador: se cargan con el día y la hora de Argentina
    // tal cual, y al guardar se leen igual. Así un operador en otro huso ve y elige la de Argentina.
    const p = partesEnArgentina(runAt);
    setDia(p ? dayjs(p.fecha) : null);
    setHora(p ? dayjs(`${p.fecha}T${p.hora}`) : null);
  };

  const elegida =
    dia && hora
      ? horaArgentina(dia.format("YYYY-MM-DD"), hora.format("HH:mm"))
      : "";
  const error = errorDeHora(elegida, new Date());
  const hoy = partesEnArgentina(new Date())?.fecha ?? "";
  const ultimo =
    partesEnArgentina(new Date(Date.now() + TAREA_MAX_DIAS * 86_400_000))
      ?.fecha ?? "";

  const guardar = async () => {
    if (error) return;
    setGuardando(true);
    try {
      if (await onGuardar(elegida)) setOpen(false);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={abrir}
      trigger={["click"]}
      placement="topLeft"
      content={
        <div style={{ width: 260 }}>
          <Text strong>Cambiar hora</Text>
          <Flex gap={6} style={{ marginTop: 8 }}>
            <DatePicker
              size="small"
              placeholder="Día"
              format="DD/MM/YYYY"
              value={dia}
              onChange={setDia}
              allowClear={false}
              disabledDate={(d) => {
                const f = d.format("YYYY-MM-DD");
                return f < hoy || f > ultimo;
              }}
              style={{ flex: 1 }}
            />
            <TimePicker
              size="small"
              placeholder="Hora"
              format="HH:mm"
              minuteStep={5}
              value={hora}
              onChange={setHora}
              allowClear={false}
              needConfirm={false}
              style={{ flex: 1 }}
            />
          </Flex>
          <Text
            type="secondary"
            style={{ display: "block", marginTop: 6, fontSize: 12 }}
          >
            {elegida && !error
              ? `Hora de Argentina: ${fechaDeTarea(elegida)}`
              : "Hora de Argentina"}
          </Text>
          {elegida && error ? (
            <Text type="danger" style={{ display: "block", fontSize: 12 }}>
              {error}
            </Text>
          ) : null}
          <Flex justify="end" gap={8} style={{ marginTop: 12 }}>
            <Button size="small" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button
              size="small"
              type="primary"
              loading={guardando}
              disabled={!!error}
              onClick={() => void guardar()}
            >
              Guardar
            </Button>
          </Flex>
        </div>
      }
    >
      {children}
    </Popover>
  );
}

/**
 * La tarea en la nota del asistente que la confirma: la hora entera y el estado. Mientras no corrió,
 * «Cambiar hora» y «Cancelar»; quién puede lo decide la API, que responde 403 si no es quien la
 * pidió ni un Admin de Operadores del Store.
 */
export function TareaDeLaNota({
  tarea,
  onCancelar,
  onCambiarHora,
}: {
  tarea: NoteTask;
  onCancelar?: (taskId: string) => Promise<void>;
  onCambiarHora?: (taskId: string, runAt: string) => Promise<boolean>;
}) {
  const [cancelando, setCancelando] = useState(false);
  const ahora = new Date();
  const estado = estadoDeTarea(tarea, ahora);
  const tocable = sePuedeCambiar(estado);
  return (
    <Flex
      align="center"
      justify="space-between"
      gap={8}
      wrap
      style={{
        marginTop: 8,
        padding: "6px 10px",
        background: "#fff",
        border: `1px solid ${AZUL_BORDE}`,
        borderRadius: 6,
      }}
    >
      <Space size={6} wrap>
        <ClockCircleOutlined style={{ color: AZUL }} />
        <Text strong style={{ fontSize: 13 }}>
          {fechaDeTarea(tarea.runAt, ahora)}
        </Text>
        <EtiquetaDeEstado tarea={tarea} ahora={ahora} />
      </Space>
      {tocable && (onCambiarHora || onCancelar) ? (
        <Space size={4}>
          {onCambiarHora ? (
            <CambiarHora
              runAt={tarea.runAt}
              onGuardar={(runAt) => onCambiarHora(tarea.taskId, runAt)}
            >
              <Button size="small">Cambiar hora</Button>
            </CambiarHora>
          ) : null}
          {onCancelar ? (
            <Popconfirm
              title="¿Cancelar la tarea?"
              description="A esa hora el asistente no va a hacer nada."
              okText="Cancelar tarea"
              cancelText="No"
              okButtonProps={{ danger: true }}
              onConfirm={async () => {
                setCancelando(true);
                try {
                  await onCancelar(tarea.taskId);
                } finally {
                  setCancelando(false);
                }
              }}
            >
              <Button size="small" danger type="text" loading={cancelando}>
                Cancelar
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      ) : null}
    </Flex>
  );
}

/** Cuántas se ven sin desplegar: la franja no le puede comer el chat al operador. */
const VISIBLES = 3;

/**
 * La franja arriba del compose: lo que el asistente tiene pendiente en esta conversación, de la más
 * próxima a la más lejana. Cada una lleva a la nota que la confirma.
 */
export function FranjaDeTareas({
  tareas,
  onIr,
}: {
  tareas: TaskView[];
  onIr: (tarea: TaskView) => void;
}) {
  const [desplegada, setDesplegada] = useState(false);
  const ahora = useAhora(tareas.length > 0);
  const pendientes = useMemo(
    () => tareasDeLaFranja(tareas, ahora),
    [tareas, ahora],
  );
  if (pendientes.length === 0) return null;
  const visibles = desplegada ? pendientes : pendientes.slice(0, VISIBLES);
  return (
    <div
      style={{
        marginBottom: 10,
        padding: "6px 10px",
        background: AZUL_FONDO,
        border: `1px solid ${AZUL_BORDE}`,
        borderRadius: 8,
      }}
    >
      <Flex align="center" gap={6}>
        <Robot2Line style={{ fontSize: 14, color: AZUL }} />
        <Text strong style={{ fontSize: 12 }}>
          {pendientes.length === 1
            ? "1 tarea programada del asistente"
            : `${pendientes.length} tareas programadas del asistente`}
        </Text>
      </Flex>
      {visibles.map((t) => (
        <Flex
          key={t.taskId}
          align="center"
          gap={6}
          role="button"
          tabIndex={0}
          onClick={() => onIr(t)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onIr(t);
            }
          }}
          style={{ marginTop: 4, cursor: "pointer", minWidth: 0 }}
        >
          <ClockCircleOutlined style={{ color: AZUL, fontSize: 12 }} />
          <Text style={{ fontSize: 12, whiteSpace: "nowrap" }}>
            {fechaDeTarea(t.runAt, ahora)}
          </Text>
          <EtiquetaDeEstado tarea={t} ahora={ahora} />
          <Text type="secondary" ellipsis style={{ fontSize: 12, minWidth: 0 }}>
            {[
              t.agentName,
              t.requestedByName ? `la pidió ${t.requestedByName}` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </Text>
        </Flex>
      ))}
      {pendientes.length > VISIBLES ? (
        <Button
          type="link"
          size="small"
          style={{ padding: 0, height: "auto", fontSize: 12, marginTop: 2 }}
          onClick={() => setDesplegada((v) => !v)}
        >
          {desplegada ? "Ver menos" : `Ver las ${pendientes.length}`}
        </Button>
      ) : null}
    </div>
  );
}
